import { LINES } from '../data/map'
import { CARD_BY_ID, type ActionCardDef, type ActionSlot, type ActionType } from '../data/actionCards'
import { useT } from '../../../i18n'

interface Props {
  cardId: string
  /** When true, render the back (column-coloured wave with dot indicator). */
  faceDown?: boolean
  /** Stack-position dot count for the back (1, 2, or 3). Ignored on the front. */
  dots?: 1 | 2 | 3
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * Tokyo Metro action card — synthesized from the deck data, no print
 * scans. Each card carries:
 *   - Top stripe: the line pair colours + label (e.g. "G/M").
 *   - Body: 1–2 action panels stacked. Each panel shows the action's
 *     icon + name, disc count, cost, and optional magnitude (e.g.
 *     "+2 spaces" for a MOVE action).
 *   - Back: same line-pair stripe, a wave pattern background, and a
 *     1/2/3-dot indicator in the centre showing which stack-third the
 *     card belongs to.
 *
 * Sized to the same 597×822 portrait aspect as the print cards so the
 * UI grid keeps its rhythm whether we show fronts or backs.
 */
export function ActionCardArt({
  cardId, faceDown = false, dots, size = 'md', className = '',
}: Props) {
  const card = CARD_BY_ID[cardId]
  if (!card) return null

  const [primary, secondary] = lineColourPair(card.column)

  return (
    <div
      className={`relative aspect-597/822 ${SIZE_HEIGHT[size]} rounded-xl overflow-hidden
                  border border-black/10 shadow-card bg-white ${className}`}
    >
      {/* Top stripe with the column-pair colours and label. */}
      <div
        className="h-3 w-full flex items-stretch"
        aria-hidden
      >
        <div className="flex-1" style={{ backgroundColor: primary }} />
        <div className="flex-1" style={{ backgroundColor: secondary }} />
      </div>
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center pointer-events-none">
        <span className="font-display font-black text-[10px] tracking-widest text-white drop-shadow-sm"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
          {card.columnLabel}
        </span>
      </div>

      {faceDown ? <Back dots={dots ?? 1} primary={primary} secondary={secondary} /> :
                  <Front card={card} />}
    </div>
  )
}

const SIZE_HEIGHT: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-28',  // ~80×112
  md: 'h-40',  // ~116×160
  lg: 'h-56',  // ~163×224
}

function Front({ card }: { card: ActionCardDef }) {
  return (
    <div className="absolute top-3 left-0 right-0 bottom-0 p-2 flex flex-col gap-1">
      {card.slots.map((slot, i) => (
        <ActionPanel key={slot.slot} slot={slot} divider={i > 0} />
      ))}
    </div>
  )
}

function ActionPanel({ slot, divider }: { slot: ActionSlot; divider: boolean }) {
  const meta = ACTION_META[slot.type]
  return (
    <div className={`flex-1 flex flex-col items-center justify-center text-center
                     ${divider ? 'border-t border-dashed border-black/10 pt-1' : ''}`}>
      <div className="text-2xl leading-none mb-0.5" aria-hidden>{meta.icon}</div>
      <div className="font-display font-black text-[11px] tracking-widest"
           style={{ color: meta.colour }}>
        {meta.label}
      </div>
      <ActionMagnitude slot={slot} />
      <div className="flex items-center gap-1 mt-1 text-[10px] font-bold text-brand-text">
        {slot.cost > 0 && <span>¥{slot.cost}</span>}
        <DiscDots count={slot.discs} />
      </div>
    </div>
  )
}

function ActionMagnitude({ slot }: { slot: ActionSlot }) {
  const t = useT()
  if (slot.value == null) return null
  let label: string | null = null
  if (slot.type === 'MOVE') {
    label = slot.value === 1
      ? t('+{n} space', { n: slot.value })
      : t('+{n} spaces', { n: slot.value })
  }
  else if (slot.type === 'SPEED')  label = t('×{n} speed', { n: slot.value })
  else if (slot.type === 'INVEST') label = t('+stock')
  if (!label) return null
  return <div className="text-[9px] text-brand-muted leading-none">{label}</div>
}

function DiscDots({ count }: { count: number }) {
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i}
              className="inline-block w-1.5 h-1.5 rounded-full bg-brand-text" />
      ))}
    </span>
  )
}

function Back({ dots, primary, secondary }: { dots: 1 | 2 | 3; primary: string; secondary: string }) {
  const t = useT()
  // Wave-stripe pattern using the column pair colours, with a centred
  // dot indicator (1/2/3) marking the stack-third.
  return (
    <div className="absolute inset-0 top-3 flex items-center justify-center"
         style={{
           backgroundImage: `linear-gradient(135deg, ${primary} 0 4px, transparent 4px 12px,
                             ${secondary} 12px 16px, transparent 16px 24px)`,
           backgroundSize: '24px 24px',
           opacity: 0.85,
         }}>
      <div className="bg-white/90 rounded-full p-2 shadow-sm flex flex-col items-center gap-1">
        <DiscDots count={dots} />
        <span className="text-[9px] font-bold text-brand-muted uppercase tracking-widest">
          {t('stack {n}', { n: dots })}
        </span>
      </div>
    </div>
  )
}

// ── Action metadata ────────────────────────────────────────────────────

const ACTION_META: Record<ActionType, { icon: string; label: string; colour: string }> = {
  MOVE:      { icon: '🏃', label: 'MOVE',      colour: '#E8881F' },
  STATION:   { icon: '🔺', label: 'STATION',   colour: '#A03020' },
  INVEST:    { icon: '📈', label: 'INVEST',    colour: '#1F6FB2' },
  START:     { icon: '🚇', label: 'START',     colour: '#0F4E8C' },
  SPEED:     { icon: '⚡', label: 'SPEED',     colour: '#1F8B3F' },
  BIKE:      { icon: '🚲', label: 'BIKE',      colour: '#3B7A3B' },
  DISCOUNT:  { icon: '⊘',  label: 'DISCOUNT',  colour: '#7B68B8' },
  BIDDING:   { icon: '✋', label: 'BIDDING',   colour: '#5C3B7A' },
  LOAN:      { icon: '💴', label: 'LOAN',      colour: '#C0303A' },
  SPECULATE: { icon: '🎲', label: 'SPECULATE', colour: '#B5841E' },
  ACTION:    { icon: '◯',  label: 'ACTION',    colour: '#5A6168' },
  CARD:      { icon: '🃏', label: 'CARD',      colour: '#D0307A' },
}

/** Two line colours per column (G/M, H/T, C/Y, Z/N, A/I, S/E). */
function lineColourPair(column: number): [string, string] {
  // column 0 → lines G+M, column 1 → H+T, etc.
  const ids: Array<[string, string]> = [
    ['G', 'M'], ['H', 'T'], ['C', 'Y'], ['Z', 'N'], ['A', 'I'], ['S', 'E'],
  ]
  const [a, b] = ids[column] ?? ids[0]
  const lookup = (id: string) => LINES.find(l => l.id === id)?.color ?? '#888'
  return [lookup(a), lookup(b)]
}

/** Stable colour lookup for action labels — used by the player strip. */
export function actionMeta(type: ActionType) { return ACTION_META[type] }
